import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {GetAnalysisFile} from 'analysis-tool/AnalysisTypes';
import {assertAndContinue} from 'Asserts';
import SignedExtFileURLProxy from 'external-files/SignedExtFileURLProxy';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import ErrorBanner from 'ui-library/banners/ErrorBanner';
import {GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import WarningIcon from 'ui-library/icons/WarningIcon';

export interface ExternalFileIFrameProps {
  file: GetAnalysisFile;
  /**
   * Warning to display to the user above the iframe, informing them that they are viewing a
   * file defined externally to Benchling.
   */
  externalFileWarning: string;
  /**
   * This should only be used if:
   * - It is necessary for Javascript to run in the iframe, AND
   * - You have confirmed with Security setting this to true if you are rendering untrusted/user-generated
   *   HTML in the iframe.
   *
   * @default false
   */
  dangerouslyAllowScriptsInIFrame?: boolean;
  /**
   * Use this to override the ExternalFile resource's Content-Type header to `text/html` if its filename's
   * mimetype is inferred as text/html. Useful when the ExternalFile resource's Content-Type is not text/html,
   * but you want to render it in an iframe anyway. Otherwise, the file will be treated as an attachment and
   * downloaded, not rendered.
   *
   * @default false
   */
  dangerouslyOverrideHTMLFileContentTypeHeader?: boolean;
}

/**
 * Render an iframe of an ExternalFile, with a warning to the user that they are viewing a file external to
 * Benchling.
 *
 * @dangerous do not use elsewhere without approval from Security.
 * @package
 */
export default function ExternalFileIFrame({
  file,
  externalFileWarning,
  dangerouslyAllowScriptsInIFrame = false,
  dangerouslyOverrideHTMLFileContentTypeHeader = false,
}: ExternalFileIFrameProps): JSX.Element {
  return (
    <SignedExtFileURLProxy
      addHtmlContentTypeHeader={dangerouslyOverrideHTMLFileContentTypeHeader}
      externalFileId={file.externalFileId!}
      url={file.downloadUrl!}
      renderWithSignedURL={(signedURL) => {
        if (signedURL === undefined) {
          return <CenteredHorizontalThrobber />;
        } else if (signedURL == null) {
          assertAndContinue(false, 'Got inaccessible file within an analysis', {
            externalFileId: file.externalFileId,
          });
          return <ErrorBanner>Error loading file.</ErrorBanner>;
        } else {
          return (
            <div className={css(styles.wrapper)}>
              <div className={css(styles.warning)}>
                <WarningIcon />
                <span>{externalFileWarning}</span>
              </div>
              <iframe
                // Use an iframe sandbox to lock down the code executed in the user-defined HTML.
                // See https://www.w3schools.com/tags/att_iframe_sandbox.asp for a list of the restrictions.
                // The default is sandbox="", which applies all restrictions.
                sandbox={dangerouslyAllowScriptsInIFrame ? 'allow-scripts' : ''}
                src={signedURL}
                className={css(styles.iframe)}
                title={file.filename}
              />
            </div>
          );
        }
      }}
      owningEntityApiId={file.id}
    />
  );
}

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  warning: {
    ...FONT_PROXIMA_13,
    background: GRAY_LIGHTER,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    padding: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  iframe: {
    flexGrow: 1,
    border: 'none',
  },
});
